import { Icon, makeStyles, Text } from '@rneui/themed'
import React, { useCallback, useState } from 'react'
import { Modal, useWindowDimensions, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import Spinner from 'shared/components/spinner'
import { ViewerProps } from '../types'

const MIN_ZOOM = 0.4
const MAX_ZOOM = 2
const ZOOM_STEP = 0.2
const DEFAULT_ZOOM = 0.8

export function PdfViewer({ source, onError, goBack, title }: ViewerProps) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`
  const styles = useStyles()
  const [pagesCount, setPagesCount] = useState<number>()
  const [visible, setVisible] = useState(true)
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  const { width, height } = useWindowDimensions()
  const basePageWidth = Math.max(Math.floor(width - 32), 280)
  const pageWidth = Math.floor(basePageWidth * zoom)

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setPagesCount(numPages)
  }

  function getPages() {
    if (!pagesCount) {
      return null
    }

    return Array.from(new Array(pagesCount), (_, index) => (
      <View key={`page_wrap_${index + 1}`} style={styles.pageContainer}>
        <Page
          pageNumber={index + 1}
          width={pageWidth}
          renderAnnotationLayer={false}
          renderTextLayer={false}
        />
        <Text style={styles.pageCounter}>
          Page {index + 1} of {pagesCount}
        </Text>
      </View>
    ))
  }

  const onPressClose = useCallback(() => {
    setVisible(false)
    goBack && goBack()
  }, [goBack])

  const onZoomIn = useCallback(() => {
    setZoom(current =>
      Math.min(MAX_ZOOM, Number((current + ZOOM_STEP).toFixed(2)))
    )
  }, [])

  const onZoomOut = useCallback(() => {
    setZoom(current =>
      Math.max(MIN_ZOOM, Number((current - ZOOM_STEP).toFixed(2)))
    )
  }, [])

  const zoomLabel = `${Math.round(zoom * 100)}%`

  return (
    <Modal style={styles.modal} visible={visible} onRequestClose={onPressClose}>
      <View style={styles.header}>
        {title ? (
          <View style={styles.headerTitle}>
            <Text numberOfLines={1} style={styles.headerTitleText}>
              {title}
            </Text>
          </View>
        ) : null}
        <PressableOpacity onPress={onPressClose}>
          <View style={styles.iconContainer}>
            <Icon
              name="times"
              size={20}
              type="font-awesome-5"
              solid
              color={styles.icon.color}
            />
          </View>
        </PressableOpacity>
      </View>

      <ScrollView
        contentContainerStyle={[styles.container, { paddingVertical: 10 }]}
      >
        <ScrollView
          contentContainerStyle={[styles.container, { paddingHorizontal: 10 }]}
          horizontal
        >
          <Document
            key={source.uri}
            onLoadError={onError}
            loading={
              <View
                style={{
                  height,
                  width,
                }}
              >
                <Spinner />
              </View>
            }
            file={source.uri}
            onLoadSuccess={onDocumentLoadSuccess}
          >
            {getPages()}
          </Document>
        </ScrollView>
      </ScrollView>
      <View style={styles.bottomControls}>
        <View style={styles.zoomControls}>
          <PressableOpacity onPress={onZoomOut} disabled={zoom <= MIN_ZOOM}>
            <View style={styles.zoomButton}>
              <Text style={styles.zoomButtonText}>-</Text>
            </View>
          </PressableOpacity>
          <Text style={styles.zoomLabel}>{zoomLabel}</Text>
          <PressableOpacity onPress={onZoomIn} disabled={zoom >= MAX_ZOOM}>
            <View style={styles.zoomButton}>
              <Text style={styles.zoomButtonText}>+</Text>
            </View>
          </PressableOpacity>
        </View>
      </View>
    </Modal>
  )
}

const useStyles = makeStyles(theme => ({
  modal: { flex: 1 },
  header: { flexDirection: 'row', backgroundColor: theme.colors.white },
  headerTitle: { flex: 1, justifyContent: 'center', padding: 15 },
  headerTitleText: { fontSize: 18, color: theme.colors.primary },
  bottomControls: {
    position: 'absolute',
    bottom: 16,
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'box-none',
  },
  zoomControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: theme.colors.grey10,
    borderWidth: 1,
    borderColor: theme.colors.grey8,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  zoomButton: {
    minWidth: 26,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  zoomButtonText: {
    color: theme.mode === 'dark' ? theme.colors.black : theme.colors.white,
    fontSize: 20,
    lineHeight: 20,
  },
  zoomLabel: {
    color: theme.mode === 'dark' ? theme.colors.black : theme.colors.white,
    minWidth: 44,
    textAlign: 'center',
  },
  pageContainer: { flex: 1, marginBottom: 10 },
  container: {
    backgroundColor: theme.colors.grey6,
    justifyContent: 'center',
    flexGrow: 1,
  },
  pageCounter: {
    flex: 1,
    justifyContent: 'center',
    alignSelf: 'center',
    textAlign: 'center',
  },
  icon: {
    color: theme.colors.primary,
  },
  iconContainer: {
    justifyContent: 'center',
    alignItems: 'flex-end',
    padding: 15,
  },
}))
